import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import cx from "classnames";
import React, { CSSProperties, useMemo } from "react";
import { isElementScrollableRight, useHandleScroll } from "../../lib/use-handle-scroll";
import { TableBody } from "./body";
import { TableHeader } from "./header";
import style from "./table.module.scss";

export interface ITableColumns {
  header: string;
  accessor: string;
  fixedWidth?: number | string;
  expandable?: boolean;
  align?: "left" | "center" | "right";
}

export type TableData = Record<ITableColumns["accessor"], React.ReactNode> & {
  subRows?: TableData[];
  isTotal?: boolean;
  className?: string;
};

export interface ITable {
  type?: "plain";

  columns: ITableColumns[];
  data?: TableData[];
  defaultRows?: number;
  header?: boolean;
  overflow?: number;
  placeholder?: boolean;
  empty?: React.ReactNode;

  tableClassName?: string;
  className?: string;
  style?: CSSProperties;
  htmlId?: string;
}

export const Table = (props: ITable) => {
  const columnHelper = createColumnHelper<TableData>();
  const { scrollRef, scrollPosition } = useHandleScroll();

  const expandableColumn = useMemo(() => {
    return props.columns.find((elem) => elem.expandable)?.accessor;
  }, [props.columns]);

  const defaultColumn = useMemo(
    () =>
      props.columns.map((elem) =>
        // @ts-ignore
        columnHelper.accessor(elem.accessor, {
          id: elem.accessor,
          header: elem.header,
          cell: (props) => props.getValue(),
        })
      ),
    [columnHelper, props.columns]
  );

  const table = useReactTable({
    columns: defaultColumn,
    data: props.data || [],
    getCoreRowModel: getCoreRowModel(),
    getSubRows: (originalData) => originalData.subRows,
  });

  return (
    <div
      className={cx(style.table_wrapper, props.type === "plain" && style.plain, props.className)}
      style={props.style}
      id={props.htmlId}
    >
      <div className={cx(style.scroller_overlay_container)}>
        <div
          className={cx(
            style.scroller_overlay,
            scrollPosition > 0 && style.left,
            isElementScrollableRight(scrollRef) && style.right
          )}
        ></div>

        <div className={cx(style.table_scroller)} ref={scrollRef}>
          <div className={style.table_tableWrapper}>
            <table className={style.table}>
              {props.header !== false && <TableHeader table={table} columns={props.columns} />}
              <TableBody
                table={table}
                header={props.columns}
                expandableColumn={expandableColumn}
                placeholder={props.placeholder}
                defaultRows={props.defaultRows}
              />
            </table>
          </div>

          {table.getRowModel().rows.length === 0 && !props.placeholder && (
            <div
              className={style.table_empty}
              style={{ height: `calc(${props.defaultRows || 1} * var(--geist-gap-double))` }}
            >
              {props.empty || "Нет данных"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Table.defaultProps = {};
